/**
 * Forms List Page
 * 
 * Dashboard page showing all forms in a professional table layout.
 * Features: search, filter, create, edit, duplicate, delete forms.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout, EmptyState } from '../../components/dashboard';
import type { FormListItem } from '@formflow/shared';
import { formsApi, ApiClientError } from '../../lib/api';
import './FormsPage.css';

export function FormsPage() {
  const navigate = useNavigate();
  const [forms, setForms] = useState<FormListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [isCreating, setIsCreating] = useState(false);
  const [newFormName, setNewFormName] = useState('');
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  // Fetch forms
  const fetchForms = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const params: Record<string, string> = {};
      if (searchQuery) params.search = searchQuery;
      if (statusFilter) params.status = statusFilter;
      
      const response = await formsApi.list(params);
      setForms(response.data || []);
    } catch (e) {
      const message = e instanceof ApiClientError
        ? e.error.message
        : 'Failed to load forms';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, statusFilter]);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdownId(null);
    if (openDropdownId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdownId]);

  // Create new form
  const handleCreateForm = async () => {
    if (!newFormName.trim()) return;
    
    try {
      const form = await formsApi.create({ name: newFormName.trim() });
      setNewFormName('');
      setIsCreating(false);
      navigate(`/dashboard/forms/${form.id}`);
    } catch (e) {
      const message = e instanceof ApiClientError
        ? e.error.message
        : 'Failed to create form';
      setError(message);
    }
  };

  // Delete form
  const handleDeleteForm = async (formId: string, formName: string) => {
    if (!confirm(`Are you sure you want to delete "${formName}"?`)) return;
    
    try {
      await formsApi.delete(formId);
      setForms(forms.filter(f => f.id !== formId));
      setOpenDropdownId(null);
    } catch (e) {
      const message = e instanceof ApiClientError
        ? e.error.message
        : 'Failed to delete form';
      setError(message);
    }
  };

  // Duplicate form
  const handleDuplicateForm = async (formId: string) => {
    try {
      const form = await formsApi.duplicate(formId);
      setForms([form as unknown as FormListItem, ...forms]);
      setOpenDropdownId(null);
    } catch (e) {
      const message = e instanceof ApiClientError
        ? e.error.message
        : 'Failed to duplicate form';
      setError(message);
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Status badge component
  const StatusBadge = ({ status }: { status: string }) => (
    <span className={`forms-status-badge forms-status-${status}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );

  // Toggle dropdown
  const toggleDropdown = (e: React.MouseEvent, formId: string) => {
    e.stopPropagation();
    setOpenDropdownId(openDropdownId === formId ? null : formId);
  };

  return (
    <DashboardLayout title="Forms" subtitle="Create and manage your forms">
      {/* Toolbar */}
      <div className="forms-toolbar">
        <div className="forms-toolbar-left">
          <div className="forms-search">
            <svg className="forms-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              type="text"
              placeholder="Search forms..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="forms-search-input"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="forms-filter-select"
          >
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
        </div>
        
        <button
          className="forms-btn forms-btn-primary"
          onClick={() => setIsCreating(true)}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Create Form
        </button>
      </div>

      {/* Create Form Modal */}
      {isCreating && (
        <div className="forms-modal-overlay" onClick={() => setIsCreating(false)}>
          <div className="forms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="forms-modal-header">
              <h2>Create New Form</h2>
              <button 
                className="forms-modal-close"
                onClick={() => setIsCreating(false)}
                aria-label="Close"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="forms-modal-body">
              <label htmlFor="formName" className="forms-label">
                Form Name
              </label>
              <input
                type="text"
                id="formName"
                value={newFormName}
                onChange={(e) => setNewFormName(e.target.value)}
                placeholder="e.g., Contact Form, Patient Intake"
                className="forms-input"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleCreateForm()}
              />
            </div>
            <div className="forms-modal-footer">
              <button
                className="forms-btn forms-btn-secondary"
                onClick={() => setIsCreating(false)}
              >
                Cancel
              </button>
              <button
                className="forms-btn forms-btn-primary"
                onClick={handleCreateForm}
                disabled={!newFormName.trim()}
              >
                Create Form
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="forms-error-banner">
          <div className="forms-error-content">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
            <span>{error}</span>
          </div>
          <button onClick={() => setError(null)} className="forms-error-dismiss">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="forms-loading">
          <div className="forms-spinner"></div>
          <p>Loading forms...</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && forms.length === 0 && !searchQuery && !statusFilter && (
        <EmptyState
          title="No forms yet"
          description="Create your first form to start collecting responses"
          actionLabel="Create Form"
          onAction={() => setIsCreating(true)}
        />
      )}

      {/* No Results */}
      {!isLoading && forms.length === 0 && (searchQuery || statusFilter) && (
        <div className="forms-no-results">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <h3>No forms found</h3>
          <p>No forms match your current filters.</p>
          <button
            className="forms-btn forms-btn-secondary"
            onClick={() => {
              setSearchQuery('');
              setStatusFilter('');
            }}
          >
            Clear filters
          </button>
        </div>
      )}

      {/* Forms Table */}
      {!isLoading && forms.length > 0 && (
        <div className="forms-table-container">
          <table className="forms-table">
            <thead>
              <tr>
                <th className="forms-th-name">Form Name</th>
                <th className="forms-th-status">Status</th>
                <th className="forms-th-submissions">Submissions</th>
                <th className="forms-th-version">Version</th>
                <th className="forms-th-updated">Last Updated</th>
                <th className="forms-th-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {forms.map((form) => (
                <tr 
                  key={form.id} 
                  className="forms-table-row"
                  onClick={() => navigate(`/dashboard/forms/${form.id}`)}
                >
                  <td className="forms-td-name">
                    <div className="forms-name-cell">
                      <div className="forms-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                          <line x1="16" y1="13" x2="8" y2="13" />
                          <line x1="16" y1="17" x2="8" y2="17" />
                          <line x1="10" y1="9" x2="8" y2="9" />
                        </svg>
                      </div>
                      <div className="forms-name-text">
                        <span className="forms-name">{form.name}</span>
                        {form.description && (
                          <span className="forms-description">{form.description}</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="forms-td-status">
                    <StatusBadge status={form.status} />
                  </td>
                  <td className="forms-td-submissions">
                    <div className="forms-submissions-cell">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                        <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                      </svg>
                      <span>{form.submission_count}</span>
                    </div>
                  </td>
                  <td className="forms-td-version">
                    {form.active_version_number ? `v${form.active_version_number}` : '—'}
                  </td>
                  <td className="forms-td-updated">
                    {formatDate(form.updated_at)}
                  </td>
                  <td className="forms-td-actions" onClick={(e) => e.stopPropagation()}>
                    <div className="forms-actions-cell">
                      <button
                        className="forms-btn forms-btn-sm forms-btn-primary"
                        onClick={() => navigate(`/dashboard/forms/${form.id}`)}
                      >
                        Edit
                      </button>
                      <button
                        className="forms-btn forms-btn-sm forms-btn-secondary"
                        onClick={() => navigate(`/dashboard/forms/${form.id}/analytics`)}
                      >
                        Analytics
                      </button>
                      <div className="forms-dropdown">
                        <button 
                          className="forms-btn forms-btn-sm forms-btn-icon"
                          onClick={(e) => toggleDropdown(e, form.id)}
                          aria-label="More options"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="1" />
                            <circle cx="12" cy="5" r="1" />
                            <circle cx="12" cy="19" r="1" />
                          </svg>
                        </button>
                        {openDropdownId === form.id && (
                          <div className="forms-dropdown-menu">
                            <button onClick={() => handleDuplicateForm(form.id)}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                              </svg>
                              Duplicate
                            </button>
                            <button
                              className="forms-dropdown-danger"
                              onClick={() => handleDeleteForm(form.id, form.name)}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                <line x1="10" y1="11" x2="10" y2="17" />
                                <line x1="14" y1="11" x2="14" y2="17" />
                              </svg>
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashboardLayout>
  );
}

export default FormsPage;
